/** Mobile only (≤767px) — clamp Lenis while pinned story sections are in progress. */
import { isMobileAnchorNavigationActive } from "@/mobileAnchorNav";
import { isMobileSectionSnapping } from "@/mobileSectionSnap";
import { isMobileViewport } from "@/mobileScrollHub";
import {
  EASE_PREMIUM_OUT,
  MOBILE_GATE_SNAP_DURATION_SEC,
} from "@/scrollPresets";

const GATE_DEFAULTS = {
  active: false,
  owner: null,
  minScrollY: -Infinity,
  maxScrollY: Infinity,
  sectionStart: 0,
  sectionEnd: Infinity,
};

let gate = { ...GATE_DEFAULTS };

/** One-shot scroll snap requested by a pinned story section (e.g. after step advance). */
let pendingSnap = null;

/** Prevents scrollTo → scroll event → scrollTo infinite recursion. */
let clamping = false;

export function setJourneyMobileScrollGate(
  active,
  minScrollY,
  maxScrollY,
  sectionStart,
  sectionEnd,
  owner = "journey",
) {
  if (active && !isMobileViewport()) return;
  if (!active) {
    if (gate.owner === owner) {
      gate = { ...GATE_DEFAULTS };
      pendingSnap = null;
    }
    return;
  }
  const min = minScrollY ?? -Infinity;
  const max = maxScrollY ?? Infinity;
  gate = {
    active: true,
    owner,
    minScrollY: Math.min(min, max),
    maxScrollY: Math.max(min, max),
    sectionStart: sectionStart ?? 0,
    sectionEnd: sectionEnd ?? Infinity,
  };
}

export function getJourneyScrollGate() {
  return gate.active
    ? {
        minScrollY: gate.minScrollY,
        maxScrollY: gate.maxScrollY,
        sectionEnd: gate.sectionEnd,
      }
    : null;
}

/** @deprecated use getJourneyScrollGate */
export function getJourneyScrollGateMax() {
  return gate.active ? gate.maxScrollY : null;
}

/** Snap Lenis to a story band (pulls overshoot back after a step commit). */
export function queueMobileScrollSnap(scrollY, owner) {
  if (!isMobileViewport() || !gate.active || gate.owner !== owner) return;
  pendingSnap = scrollY;
}

/** Clamp Lenis when scroll overshoots the allowed story band. */
export function applyJourneyScrollGate(lenis) {
  if (
    !isMobileViewport() ||
    !gate.active ||
    !lenis ||
    clamping ||
    isMobileAnchorNavigationActive() ||
    isMobileSectionSnapping()
  ) {
    return;
  }

  const y = lenis.scroll;
  let target = null;
  let isStoryBandSnap = false;
  const ceiling = Math.min(gate.maxScrollY, gate.sectionEnd);
  if (pendingSnap != null) {
    target = pendingSnap;
    isStoryBandSnap = true;
    pendingSnap = null;
  } else if (y > ceiling + 6) {
    target = ceiling;
  } else if (y < gate.minScrollY - 6) {
    target = gate.minScrollY;
  }
  if (target == null) return;

  clamping = true;
  try {
    lenis.scrollTo(target, {
      immediate: !isStoryBandSnap,
      duration: isStoryBandSnap ? MOBILE_GATE_SNAP_DURATION_SEC : undefined,
      easing: isStoryBandSnap ? EASE_PREMIUM_OUT : undefined,
      force: true,
      programmatic: true,
    });
  } finally {
    clamping = false;
  }
}
